/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.runtime

import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.KlangScriptEngine
import io.peekandpoke.klang.script.ast.Argument
import io.peekandpoke.klang.script.ast.ArrayLiteral
import io.peekandpoke.klang.script.ast.ArrowFunction
import io.peekandpoke.klang.script.ast.ArrowFunctionBody
import io.peekandpoke.klang.script.ast.AssignmentExpression
import io.peekandpoke.klang.script.ast.BinaryOperation
import io.peekandpoke.klang.script.ast.BinaryOperator
import io.peekandpoke.klang.script.ast.BooleanLiteral
import io.peekandpoke.klang.script.ast.BreakStatement
import io.peekandpoke.klang.script.ast.CallExpression
import io.peekandpoke.klang.script.ast.ConstDeclaration
import io.peekandpoke.klang.script.ast.ContinueStatement
import io.peekandpoke.klang.script.ast.DoWhileStatement
import io.peekandpoke.klang.script.ast.ElseBranch
import io.peekandpoke.klang.script.ast.ExportDeclaration
import io.peekandpoke.klang.script.ast.ExportStatement
import io.peekandpoke.klang.script.ast.Expression
import io.peekandpoke.klang.script.ast.ExpressionStatement
import io.peekandpoke.klang.script.ast.ForStatement
import io.peekandpoke.klang.script.ast.Identifier
import io.peekandpoke.klang.script.ast.IfExpression
import io.peekandpoke.klang.script.ast.ImportStatement
import io.peekandpoke.klang.script.ast.IndexAccess
import io.peekandpoke.klang.script.ast.LetDeclaration
import io.peekandpoke.klang.script.ast.MemberAccess
import io.peekandpoke.klang.script.ast.NullLiteral
import io.peekandpoke.klang.script.ast.NumberLiteral
import io.peekandpoke.klang.script.ast.ObjectLiteral
import io.peekandpoke.klang.script.ast.Program
import io.peekandpoke.klang.script.ast.ReturnStatement
import io.peekandpoke.klang.script.ast.Statement
import io.peekandpoke.klang.script.ast.StringLiteral
import io.peekandpoke.klang.script.ast.TemplateLiteral
import io.peekandpoke.klang.script.ast.TemplatePart
import io.peekandpoke.klang.script.ast.TernaryExpression
import io.peekandpoke.klang.script.ast.UnaryOperation
import io.peekandpoke.klang.script.ast.UnaryOperator
import io.peekandpoke.klang.script.ast.WhileStatement
import io.peekandpoke.klang.script.parser.KlangScriptParser
import io.peekandpoke.klang.script.utils.formatAvailableNames
import io.peekandpoke.klang.script.utils.suggestNames
import kotlin.math.pow

/**
 * Interface for loading library source code
 *
 * This allows the interpreter to load libraries without directly depending
 * on the KlangScript engine class.
 */
interface LibraryLoader {
    /**
     * Load library source code by name
     *
     * @param name The library name
     * @return The KlangScript source code for the library
     * @throws RuntimeException if the library is not found
     */
    fun loadLibrary(name: String): String
}

/**
 * Tree-walking interpreter for KlangScript
 *
 * This interpreter executes KlangScript programs by walking the AST and
 * evaluating nodes recursively. It's called "tree-walking" because it
 * traverses the syntax tree directly without compiling to bytecode.
 *
 * Execution flow:
 * 1. Parse source code → AST (done by parser)
 * 2. Walk the AST and evaluate each node
 * 3. Return the final result
 *
 * The interpreter uses a visitor pattern, with separate methods for
 * statements and expressions. All expressions evaluate to RuntimeValues.
 *
 * Example:
 * ```kotlin
 * val context = ExecutionContext(sourceName = "main.klang")
 * val interpreter = Interpreter(executionContext = context, engine = engine)
 * val program = KlangScriptParser.parse("print('hello')")
 * interpreter.execute(program)
 * ```
 */
class Interpreter(
    /** The environment for variable and function storage */
    private val env: Environment = Environment(),
    /** Optional library loader for import statements */
    private val engine: KlangScriptEngine,
    /** Call stack for tracking function calls and generating stack traces */
    private val callStack: CallStack = CallStack(),
    /** Execution context with source metadata and current location */
    private val executionContext: ExecutionContext,
) {
    /**
     * Get the current stack trace
     *
     * Returns a snapshot of the current call stack for error reporting.
     */
    private fun getStackTrace(): List<CallStackFrame> = callStack.getFrames()

    /**
     * Execute a complete program
     *
     * Executes each statement in the program sequentially.
     * The value of the last statement is returned as the program's result.
     *
     * @param program The parsed AST program to execute
     * @return The runtime value of the last statement, or NullValue if empty
     *
     * Example:
     * ```
     * // Script: print("a"); print("b"); 42
     * // Returns: NumberValue(42.0)
     * ```
     */
    fun execute(program: Program): RuntimeValue {
        var lastValue: RuntimeValue = NullValue

        for (statement in program.statements) {
            lastValue = executeStatement(statement)
        }

        return lastValue
    }

    /**
     * Execute a block of statements and return the value of the last ExpressionStatement.
     *
     * This is the mechanism by which if/else branches produce values.
     * Returns NullValue if the block is empty or the last statement is not an ExpressionStatement.
     *
     * @param statements The block of statements to execute
     * @return The value of the last ExpressionStatement, or NullValue
     */
    internal fun executeBlock(statements: List<Statement>): RuntimeValue {
        var lastValue: RuntimeValue = NullValue
        for (stmt in statements) {
            lastValue = executeStatement(stmt)
        }
        return lastValue
    }

    /**
     * Execute a block of statements in a fresh child scope.
     *
     * Used for loop bodies so that `let` declarations inside a loop do not
     * leak into the enclosing scope and are re-bound on every iteration.
     */
    private fun executeBlockInChildScope(statements: List<Statement>): RuntimeValue {
        val childEnv = Environment(parent = env)
        val childInterp = Interpreter(env = childEnv, engine = engine, callStack = callStack, executionContext = executionContext)
        return childInterp.executeBlock(statements)
    }

    /**
     * Execute one loop-body iteration, handling break/continue signals.
     *
     * Returns true  → keep iterating (normal completion or `continue`)
     * Returns false → stop the loop  (`break`)
     * Throws        → propagate `ReturnException` (and any other exception) upward
     */
    private fun executeLoopBody(statements: List<Statement>): Boolean {
        return try {
            executeBlockInChildScope(statements)
            true
        } catch (_: BreakException) {
            false
        } catch (_: ContinueException) {
            true
        }
    }

    /**
     * Execute a single statement
     *
     * Statements represent actions in the program but may also produce values.
     *
     * @param statement The statement to execute
     * @return The runtime value produced by the statement
     * @throws ReturnException if a return statement is encountered
     */
    internal fun executeStatement(statement: Statement): RuntimeValue {
        return when (statement) {
            // Expression statement: evaluate the expression
            is ExpressionStatement -> evaluate(statement.expression)

            // Let declaration: create mutable variable
            is LetDeclaration -> {
                val value = if (statement.initializer != null) {
                    evaluate(statement.initializer)
                } else {
                    NullValue
                }
                env.define(statement.name, value, mutable = true)
                NullValue  // Declarations don't produce values
            }

            // Const declaration: create immutable variable
            is ConstDeclaration -> {
                val value = evaluate(statement.initializer)
                env.define(statement.name, value, mutable = false)
                NullValue  // Declarations don't produce values
            }

            // Import statement: load and evaluate library, import symbols
            is ImportStatement -> executeImport(statement)

            // Export statement: mark symbols for export
            is ExportStatement -> executeExport(statement)

            // Export declaration: const-like binding + auto-export under same name.
            // Evaluates to the bound value (unlike let/const which return null) so that
            // `export song = stack(...)` as the last statement of a library file makes
            // the script return the song without needing a trailing `song` reference.
            is ExportDeclaration -> {
                val value = evaluate(statement.initializer)
                env.define(statement.name, value, mutable = false)
                env.markExports(listOf(statement.name to statement.name))
                value
            }

            // Return statement: throw ReturnException to exit function
            is ReturnStatement -> {
                val returnValue = if (statement.value != null) {
                    evaluate(statement.value)
                } else {
                    NullValue
                }
                throw ReturnException(returnValue)
            }

            // While loop: condition evaluated in outer scope, body in fresh child scope per iteration
            is WhileStatement -> {
                while (toBoolean(evaluate(statement.condition))) {
                    if (!executeLoopBody(statement.body)) break
                }
                NullValue
            }

            // Do-while loop: body in fresh child scope per iteration, then condition in outer scope
            is DoWhileStatement -> {
                do {
                    if (!executeLoopBody(statement.body)) break
                } while (toBoolean(evaluate(statement.condition)))
                NullValue
            }

            // For loop: init + condition + update in loop scope; body in fresh child scope per iteration
            is ForStatement -> {
                val loopEnv = Environment(parent = env)
                val loopInterp = Interpreter(env = loopEnv, engine = engine, callStack = callStack, executionContext = executionContext)
                statement.init?.let { loopInterp.executeStatement(it) }
                loop@ while (statement.condition == null || loopInterp.toBoolean(loopInterp.evaluate(statement.condition))) {
                    if (!loopInterp.executeLoopBody(statement.body)) break@loop
                    statement.update?.let { loopInterp.evaluate(it) }
                }
                NullValue
            }

            // Break statement - throws BreakException to exit enclosing loop
            is BreakStatement -> throw BreakException()

            // Continue statement - throws ContinueException to skip to next loop iteration
            is ContinueStatement -> throw ContinueException()
        }
    }

    /**
     * Execute an import statement
     *
     * Process:
     * 1. Load the library source code from the library loader
     * 2. Parse the library source code
     * 3. Create an isolated environment for library evaluation
     * 4. Execute the library in the isolated environment
     * 5. Import all top-level symbols from the library environment into current scope
     *
     * @param importStmt The import statement AST node
     * @return NullValue (imports don't produce values)
     * @throws RuntimeException if library loader is not available or library not found
     *
     * Example:
     * ```javascript
     * // strudel.klang library:
     * let note = (pattern) => { /* ... */ }
     * let sound = (pattern) => { /* ... */ }
     *
     * // User script:
     * import * from "strudel"
     * note("a b c")  // note() is now available
     * ```
     */
    private fun executeImport(importStmt: ImportStatement): RuntimeValue {
        val libName = importStmt.libraryName

        val librarySource = try {
            engine.loadLibrary(libName)
        } catch (e: Exception) {
            throw KlangScriptImportError(
                libraryName = libName,
                message = "Failed to load library '$libName': ${e.message}",
                location = importStmt.location,
                callStackTrace = getStackTrace()
            )
        }

        // Parse library source code — pass the library name as the source so all
        // SourceLocations produced from imported code carry it. The editor uses
        // this to filter highlight events to the file currently displayed.
        val libraryProgram = KlangScriptParser.parse(librarySource, importStmt.libraryName)

        // Create an isolated environment / scope for library evaluation,
        // starting with the native environment of the engine.
        val libraryEnv = Environment(parent = engine.nativeEnvironment)

        // Execute the library in an isolated environment with shared execution context
        val libraryInterpreter = Interpreter(
            env = libraryEnv,
            engine = engine,
            callStack = CallStack(),
            executionContext = executionContext
        )
        libraryInterpreter.execute(libraryProgram)

        // Import symbols from library environment into current environment
        importSymbolsFromEnvironment(libraryEnv, importStmt.imports, importStmt.namespaceAlias, importStmt.libraryName, importStmt.location)

        return NullValue  // Imports don't produce values
    }

    /**
     * Import symbols from a library environment into the current environment
     *
     * This copies exported symbols from the library into the current scope.
     * Supports wildcard, namespace, and selective imports with optional aliasing.
     *
     * @param libraryEnv The library environment containing symbols to import
     * @param imports List of (exportName, localAlias) pairs for selective imports (null for wildcard)
     * @param namespaceAlias If set, creates a namespace object instead of importing into current scope
     * @param libraryName The name of the library being imported (for error messages)
     */
    private fun importSymbolsFromEnvironment(
        libraryEnv: Environment,
        imports: List<Pair<String, String>>?,
        namespaceAlias: String?,
        libraryName: String,
        importLocation: SourceLocation? = null,
    ) {
        // Get exported symbols from library
        val exports = libraryEnv.getExportedSymbols()

        if (namespaceAlias != null) {
            // Namespace import - create an object containing all exports
            if (imports != null) {
                throw KlangScriptImportError(
                    libraryName = null,
                    message = "Cannot use namespace import with selective imports",
                    location = importLocation,
                    callStackTrace = getStackTrace()
                )
            }
            val namespaceObject = ObjectValue(exports.toMutableMap())
            env.define(namespaceAlias, namespaceObject, mutable = true)
        } else if (imports == null) {
            // Wildcard import - import all exports with their original names
            for ((name, value) in exports) {
                env.define(name, value, mutable = true)
            }
        } else {
            // Selective import - import only specified names, applying aliases
            val exportNames = imports.map { it.first }
            val missingExports = exportNames.filter { it !in exports }
            if (missingExports.isNotEmpty()) {
                throw KlangScriptImportError(
                    libraryName = libraryName,
                    message = "Cannot import non-exported symbols: ${missingExports.joinToString()}",
                    location = importLocation,
                    callStackTrace = getStackTrace()
                )
            }

            // Import each symbol with its alias
            for ((exportName, localAlias) in imports) {
                val value = exports[exportName]!!  // Safe because we validated above
                env.define(localAlias, value, mutable = true)
            }
        }
    }

    /**
     * Execute an export statement
     *
     * Marks the specified symbols as exported from the current environment,
     * optionally with different names (aliasing).
     * Only exported symbols will be accessible when this code is loaded as a library.
     *
     * @param exportStmt The export statement AST node
     * @return NullValue (exports don't produce values)
     *
     * Example:
     * ```javascript
     * let add = (a, b) => a + b
     * let subtract = (a, b) => a - b
     * let internal = (x) => x * 2  // Not exported
     * export { add, subtract }  // Export with original names
     *
     * // Or with aliases:
     * export { add as sum }  // Export 'add' as 'sum'
     * ```
     */
    private fun executeExport(exportStmt: ExportStatement): RuntimeValue {
        // Mark the symbols as exported in the environment with their aliases
        env.markExports(exportStmt.exports)
        return NullValue  // Exports don't produce values
    }

    /**
     * Evaluate an expression to a runtime value
     *
     * This is the core of the interpreter. It recursively evaluates
     * expressions by pattern matching on the expression type.
     *
     * @param expression The expression AST node to evaluate
     * @return The runtime value that the expression evaluates to
     */
    fun evaluate(expression: Expression): RuntimeValue {
        return when (expression) {
            // Literals evaluate to themselves (preserving source location)
            is NumberLiteral -> NumberValue(expression.value, location = expression.location)
            is StringLiteral -> StringValue(expression.value, location = expression.location)
            is BooleanLiteral -> BooleanValue(expression.value)
            is NullLiteral -> NullValue

            // Identifiers look up variables in the environment
            is Identifier -> env.get(expression.name, expression.location, getStackTrace())

            // Function calls are delegated to evaluateCall
            is CallExpression -> evaluateCall(expression)

            // Binary operations are delegated to evaluateBinaryOp
            is BinaryOperation -> evaluateBinaryOp(expression)

            // Unary operations are delegated to evaluateUnaryOp
            is UnaryOperation -> evaluateUnaryOp(expression)

            // Member access is delegated to evaluateMemberAccess
            is MemberAccess -> evaluateMemberAccess(expression)

            // Arrow functions create function values with closure
            is ArrowFunction -> FunctionValue(
                parameters = expression.parameters,
                body = expression.body,
                closureEnv = env,
                engine = engine,
            )

            // Object literals create object values
            is ObjectLiteral -> evaluateObjectLiteral(expression)

            // Array literals create array values
            is ArrayLiteral -> evaluateArrayLiteral(expression)

            // Assignment expressions: x = value, obj.prop = value, arr[i] = value
            is AssignmentExpression -> evaluateAssignment(expression)

            // Ternary: condition ? thenExpr : elseExpr
            is TernaryExpression -> evaluateTernary(expression)

            // Index access: arr[i], obj["key"]
            is IndexAccess -> evaluateIndexAccess(expression)

            // If expression: if (cond) { ... } else { ... }
            // Each branch runs in its own child scope so that `let`/`const` declarations
            // inside a branch do not leak into the enclosing scope.
            is IfExpression -> {
                val cond = evaluate(expression.condition)
                if (toBoolean(cond)) {
                    executeBlockInChildScope(expression.thenBranch)
                } else {
                    when (val eb = expression.elseBranch) {
                        is ElseBranch.Block -> executeBlockInChildScope(eb.statements)
                        is ElseBranch.If -> evaluate(eb.ifExpr)
                        null -> NullValue
                    }
                }
            }

            // Template literal: `Hello ${name}!`
            is TemplateLiteral -> {
                val sb = StringBuilder()
                for (part in expression.parts) {
                    when (part) {
                        is TemplatePart.Text -> sb.append(part.value)
                        is TemplatePart.Interp -> sb.append(evaluate(part.expression).toDisplayString())
                    }
                }
                StringValue(sb.toString())
            }
        }
    }

    /**
     * Evaluate a function call expression
     *
     * Handles both native Kotlin functions and script-defined arrow functions.
     *
     * Process:
     * 1. Evaluate the callee (what we're calling) expression
     * 2. Verify it's actually a function
     * 3. Evaluate all argument expressions
     * 4. Call the function with the evaluated arguments
     * 5. Return the function's result
     *
     * For script functions (arrow functions):
     * - Create a new environment extending the function's closure
     * - Bind parameters to argument values in the new environment
     * - Evaluate the function body in the new environment
     * - Restore the previous environment
     *
     * @param call The call expression AST node
     * @return The runtime value returned by the function
     * @throws RuntimeException if callee is not a function or argument count mismatch
     *
     * Example (native function):
     * ```
     * // Script: print("hello")
     * // 1. Evaluate "print" → NativeFunctionValue
     * // 2. Evaluate "hello" → StringValue
     * // 3. Call native function → NullValue
     * ```
     *
     * Example (script function):
     * ```
     * // Script: (x => x + 1)(5)
     * // 1. Evaluate arrow function → FunctionValue
     * // 2. Evaluate 5 → NumberValue(5.0)
     * // 3. Create new environment with x=5
     * // 4. Evaluate body: x + 1 → NumberValue(6.0)
     * ```
     */
    private fun evaluateCall(call: CallExpression): RuntimeValue {
        // Evaluate what we're calling (usually an identifier)
        val callee = try {
            evaluate(call.callee)
        } catch (e: KlangScriptReferenceError) {
            // Improve error message: "Undefined function" instead of "Undefined symbol"
            if (call.callee is Identifier) {
                throw KlangScriptReferenceError(
                    symbolName = e.symbolName,
                    message = "Undefined function: ${e.symbolName}",
                    location = e.location,
                    astNode = call,
                    callStackTrace = e.callStackTrace,
                )
            }
            throw e
        }

        val calleeName = resolveCalleeName(callee, call.callee)

        // Evaluate every argument left-to-right, preserving Positional vs Named distinction.
        val evaluated: List<EvaluatedArgument> = call.arguments.map { arg ->
            when (arg) {
                is Argument.Positional -> EvaluatedArgument.Positional(evaluate(arg.value), arg.location)
                is Argument.Named -> EvaluatedArgument.Named(arg.name, evaluate(arg.value), arg.nameLocation)
            }
        }

        // Classify: all-positional, all-named, or Empty.
        // Throws KlangScriptArgumentError if mixed or if a name is duplicated.
        val callArgs = CallArgs.resolve(
            functionName = calleeName,
            evaluated = evaluated,
            callLocation = call.location,
            callStackTrace = getStackTrace(),
        )

        return when (callee) {
            is NativeFunctionValue -> {
                callStack.push("<native>", call.location)
                val previousLocation = executionContext.currentLocation
                executionContext.currentLocation = call.location

                try {
                    val positional = positionalArgsForNative(callee.name, callee.paramSpecs, callArgs, call)
                    guardNativeCall(callee.name, positional, call.location) {
                        callee.function(positional, call.location)
                    }
                } finally {
                    executionContext.currentLocation = previousLocation
                    callStack.pop()
                }
            }

            is FunctionValue -> {
                val boundArgs = bindScriptFunctionArgs(callee, callArgs, call)

                callStack.push("<anonymous>", call.location)
                try {
                    val funcEnv = Environment(callee.closureEnv)
                    callee.parameters.zip(boundArgs).forEach { (param, arg) ->
                        funcEnv.define(param, arg)
                    }

                    val funcInterpreter = Interpreter(funcEnv, engine, callStack, executionContext)

                    when (val body = callee.body) {
                        is ArrowFunctionBody.ExpressionBody -> funcInterpreter.evaluate(body.expression)

                        is ArrowFunctionBody.BlockBody -> {
                            try {
                                for (stmt in body.statements) {
                                    funcInterpreter.executeStatement(stmt)
                                }
                                NullValue
                            } catch (e: ReturnException) {
                                e.value
                            }
                        }
                    }
                } finally {
                    callStack.pop()
                }
            }

            is BoundNativeMethod -> {
                val fnName = "${callee.receiver.qualifiedName}.${callee.methodName}"
                callStack.push(fnName, call.location)

                val previousLocation = executionContext.currentLocation
                executionContext.currentLocation = call.location

                try {
                    val positional = positionalArgsForNative(fnName, callee.paramSpecs, callArgs, call)
                    guardNativeCall(fnName, positional, call.location) {
                        callee.invoker(positional, call.location)
                    }
                } finally {
                    executionContext.currentLocation = previousLocation
                    callStack.pop()
                }
            }

            // A native object is callable when its type registers a call form under the internal
            // symbol `__invoke__` (`Katalyst(k => ...)`, declared with `@KlangScript.Invoke`). It runs
            // through the SAME spec-aware path as a member call, so named arguments, default thunks and
            // the trailing-lambda rule all apply. The error for one without speaks to the script user.
            is NativeObjectValue<*> -> {
                val held = callee.value
                val invoke = engine.getExtensionMethod(callee, NativeOperatorNames.INVOKE)

                // A Kotlin function a native handed back (`Ign.fn(x => ...)`, a pattern mapper): called as the script
                // lambda it may have been, its arguments in order, its result a script value again
                if (invoke == null && held is Function<*>) {
                    return callHeldFunction(held, callArgs, call)
                }

                invoke ?: throw KlangScriptTypeError(
                        message = notCallableMessage(callee, call.callee),
                        operation = "function call",
                        location = call.location,
                        astNode = call,
                        callStackTrace = getStackTrace(),
                    )
                // calleeName already reads `Katalyst`, the call the user wrote (see resolveCalleeName), so
                // every error raised for this call site, mixed-style or unknown-parameter, names the same thing.
                val fnName = calleeName
                callStack.push(fnName, call.location)

                val previousLocation = executionContext.currentLocation
                executionContext.currentLocation = call.location

                try {
                    val positional = positionalArgsForNative(fnName, invoke.paramSpecs, callArgs, call)
                    guardNativeCall(fnName, positional, call.location) {
                        invoke.invoker(callee.value, positional, call.location, engine)
                    }
                } finally {
                    executionContext.currentLocation = previousLocation
                    callStack.pop()
                }
            }

            else -> {
                throw KlangScriptTypeError(
                    message = notCallableMessage(callee, call.callee),
                    operation = "function call",
                    location = call.location,
                    astNode = call,
                    callStackTrace = getStackTrace()
                )
            }
        }
    }

    /**
     * Calls [fn], a Kotlin function a native handed back, with the call's positional arguments (KLANG-00382 neighbour:
     * `Ign.fn` returns its stage as a Kotlin function, the maintainer, 2026-10-28). Up to three arguments; named ones,
     * or more, are a script error.
     */
    @Suppress("UNCHECKED_CAST")
    private fun callHeldFunction(fn: Function<*>, callArgs: CallArgs, call: CallExpression): RuntimeValue {
        val values = (callArgs as? CallArgs.Positional)?.values
            ?: throw KlangScriptTypeError(
                message = "This function takes its arguments in order, not by name.",
                operation = "function call",
                location = call.location,
                astNode = call,
                callStackTrace = getStackTrace(),
            )

        val args = values.map { if (it is NullValue) null else it.convertToKotlin(Any::class, call.location) }

        val result = try {
            when (args.size) {
                0 -> (fn as () -> Any?)()
                1 -> (fn as (Any?) -> Any?)(args[0])
                2 -> (fn as (Any?, Any?) -> Any?)(args[0], args[1])
                3 -> (fn as (Any?, Any?, Any?) -> Any?)(args[0], args[1], args[2])
                else -> throw ClassCastException("arity")
            }
        } catch (e: ClassCastException) {
            throw KlangScriptTypeError(
                message = "This function cannot be called with ${args.size} argument(s).",
                operation = "function call",
                location = call.location,
                astNode = call,
                callStackTrace = getStackTrace(),
            )
        }

        return wrapAsRuntimeValue(result)
    }

    /** User-visible name for a callee: used in argument error messages. */
    private fun resolveCalleeName(callee: RuntimeValue, calleeExpr: Expression): String = when (callee) {
        is NativeFunctionValue -> callee.name
        is BoundNativeMethod -> "${callee.receiver.qualifiedName}.${callee.methodName}"
        is FunctionValue -> writtenName(calleeExpr) ?: "<anonymous function>"
        // A callable object: named as the user calls it (`Katalyst(...)`), never by its internal
        // `__invoke__` symbol.
        is NativeObjectValue<*> -> writtenName(calleeExpr) ?: "<object>"
        else -> writtenName(calleeExpr) ?: "<anonymous>"
    }

    /** The name a call was written with (`x(...)`, `a.x(...)`), or null when the callee is an expression. */
    private fun writtenName(calleeExpr: Expression): String? = when (calleeExpr) {
        is Identifier -> calleeExpr.name
        is MemberAccess -> calleeExpr.property
        else -> null
    }

    /**
     * The one wording for calling what is not a function, a native object without a call form included:
     * named as written (`'x' cannot be called`), else by its value (`5 cannot be called`).
     */
    private fun notCallableMessage(callee: RuntimeValue, calleeExpr: Expression): String {
        val what = writtenName(calleeExpr)?.let { "'$it'" }
            ?: if (callee is NativeObjectValue<*>) "This value" else callee.toDisplayString()

        return "$what cannot be called: it is not a function."
    }

    /**
     * Convert [CallArgs] into the positional `List<RuntimeValue>` a native function receives.
     *
     *  - **With parameter specs** (every KSP-generated door and every builder registration):
     *    [resolveByParamSpec] binds positional and named arguments alike, and an omitted optional
     *    is filled from its default thunk, so the native receives one value per spec, whatever
     *    the call style. Every optional spec has a thunk: [ParamSpec] refuses one without, and the
     *    KSP processor refuses a door default it cannot turn into one (since 2026-10-06).
     *  - **Without specs** (raw registrations, zero-parameter builders, the vararg emitters): a
     *    positional call passes through unchanged, an empty call is an empty list, and a named call
     *    is an error.
     */
    private fun positionalArgsForNative(
        functionName: String,
        paramSpecs: List<ParamSpec>?,
        args: CallArgs,
        call: CallExpression,
    ): List<RuntimeValue> {
        // An empty specs list means "no metadata", the same as null.
        val specs: List<ParamSpec>? = paramSpecs?.takeIf { it.isNotEmpty() }

        if (specs != null) {
            val resolved = resolveByParamSpec(
                functionName = functionName,
                specs = specs,
                args = args,
                callLocation = call.location,
                callStackTrace = getStackTrace(),
            )

            return resolved.mapIndexed { i, v ->
                v ?: if (specs[i].isVararg) ArrayValue(mutableListOf()) else specs[i].default!!.invoke()
            }
        }

        return when (args) {
            CallArgs.Empty -> emptyList()

            is CallArgs.Positional -> args.values

            is CallArgs.Named -> throw KlangScriptArgumentError(
                functionName = functionName,
                message = "Native function '$functionName' does not yet support named arguments. " +
                        "Pass them positionally for now (named-arg support lands with the builder rewrite).",
                location = call.location,
                astNode = call,
                callStackTrace = getStackTrace(),
            )
        }
    }

    /**
     * Bind a [CallArgs] against the parameter list of a script arrow function,
     * producing a positionally-ordered list aligned with [FunctionValue.parameters].
     *
     * Script params are all required in Phase 3 — script-side defaults are
     * deferred to a later phase.
     */
    private fun bindScriptFunctionArgs(
        fn: FunctionValue,
        args: CallArgs,
        call: CallExpression,
    ): List<RuntimeValue> {
        val paramNames = fn.parameters
        val fnDisplay = "<anonymous function>"

        return when (args) {
            CallArgs.Empty -> {
                if (paramNames.isNotEmpty()) {
                    throw KlangScriptArgumentError(
                        functionName = fnDisplay,
                        message = "Function expects ${paramNames.size} arguments, got 0",
                        expected = paramNames.size,
                        actual = 0,
                        location = call.location,
                        astNode = call,
                        callStackTrace = getStackTrace(),
                    )
                }
                emptyList()
            }

            is CallArgs.Positional -> {
                if (args.values.size != paramNames.size) {
                    throw KlangScriptArgumentError(
                        functionName = fnDisplay,
                        message = "Function expects ${paramNames.size} arguments, got ${args.values.size}",
                        expected = paramNames.size,
                        actual = args.values.size,
                        location = call.location,
                        astNode = call,
                        callStackTrace = getStackTrace(),
                    )
                }
                args.values
            }

            is CallArgs.Named -> {
                val result = arrayOfNulls<RuntimeValue>(paramNames.size)
                for ((name, v) in args.values) {
                    val idx = paramNames.indexOf(name)
                    if (idx == -1) {
                        throw KlangScriptArgumentError(
                            functionName = fnDisplay,
                            message = "unknown parameter '$name' (expected: ${paramNames.joinToString(", ")})",
                            location = call.location,
                            astNode = call,
                            callStackTrace = getStackTrace(),
                        )
                    }
                    result[idx] = v
                }
                paramNames.forEachIndexed { i, name ->
                    if (result[i] == null) {
                        throw KlangScriptArgumentError(
                            functionName = fnDisplay,
                            message = "missing required parameter '$name'",
                            location = call.location,
                            astNode = call,
                            callStackTrace = getStackTrace(),
                        )
                    }
                }
                @Suppress("UNCHECKED_CAST")
                result.toList() as List<RuntimeValue>
            }
        }
    }

    /**
     * Convert a RuntimeValue to boolean using JavaScript-like truthiness rules
     *
     * Falsy values: null, false, 0, NaN, ""
     * Truthy values: everything else (non-zero numbers, non-empty strings, objects, arrays, functions)
     *
     * @param value The runtime value to convert
     * @return true if the value is truthy, false if falsy
     */
    internal fun toBoolean(value: RuntimeValue): Boolean {
        return when (value) {
            is BooleanValue -> value.value
            is NullValue -> false
            is NumberValue -> value.value != 0.0 && !value.value.isNaN()
            is StringValue -> value.value.isNotEmpty()
            else -> true  // Objects, arrays, and functions are truthy
        }
    }

    /**
     * Evaluate a unary operation expression
     *
     * Handles prefix operators: -, +, !
     *
     * Process:
     * 1. Evaluate the operand expression
     * 2. Apply the operator based on its type
     * 3. Return the result
     *
     * Operator semantics:
     * - NEGATE: Flips the sign of a number (requires NumberValue)
     * - PLUS: Identity operation, returns the number unchanged (requires NumberValue)
     * - NOT: Logical negation (converts to boolean, then negates)
     *
     * @param unaryOp The unary operation AST node
     * @return The runtime value after applying the operator
     * @throws RuntimeException if type mismatch occurs
     *
     * Examples:
     * - -5 → NumberValue(-5.0)
     * - +42 → NumberValue(42.0)
     * - !true → BooleanValue(false)
     * - !null → BooleanValue(true)
     */
    private fun evaluateUnaryOp(unaryOp: UnaryOperation): RuntimeValue {
        return when (unaryOp.operator) {
            UnaryOperator.NEGATE -> {
                val operandValue = evaluate(unaryOp.operand)
                if (operandValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Negation operator requires a number, got ${operandValue.toDisplayString()}",
                        operation = "unary -",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                NumberValue(-operandValue.value)
            }

            UnaryOperator.PLUS -> {
                val operandValue = evaluate(unaryOp.operand)
                if (operandValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Unary plus operator requires a number, got ${operandValue.toDisplayString()}",
                        operation = "unary +",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                NumberValue(operandValue.value)
            }

            UnaryOperator.NOT -> {
                val operandValue = evaluate(unaryOp.operand)
                val boolValue = toBoolean(operandValue)
                BooleanValue(!boolValue)
            }

            UnaryOperator.BITWISE_NOT -> {
                val operandValue = evaluate(unaryOp.operand)
                if (operandValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Bitwise NOT operator requires a number, got ${operandValue.toDisplayString()}",
                        operation = "unary ~",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                NumberValue(operandValue.value.toInt().inv().toDouble())
            }

            UnaryOperator.PREFIX_INCREMENT -> {
                // ++x: add 1, assign back, return new value
                val target = unaryOp.operand
                if (target !is Identifier) {
                    throw KlangScriptTypeError(
                        message = "Prefix increment (++) requires an identifier target",
                        operation = "prefix ++",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val current = evaluate(target)
                if (current !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Prefix increment (++) requires a number, got ${current.toDisplayString()}",
                        operation = "prefix ++",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val newValue = NumberValue(current.value + 1.0)
                env.assign(target.name, newValue, unaryOp.location, getStackTrace())
                newValue
            }

            UnaryOperator.PREFIX_DECREMENT -> {
                // --x: subtract 1, assign back, return new value
                val target = unaryOp.operand
                if (target !is Identifier) {
                    throw KlangScriptTypeError(
                        message = "Prefix decrement (--) requires an identifier target",
                        operation = "prefix --",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val current = evaluate(target)
                if (current !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Prefix decrement (--) requires a number, got ${current.toDisplayString()}",
                        operation = "prefix --",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val newValue = NumberValue(current.value - 1.0)
                env.assign(target.name, newValue, unaryOp.location, getStackTrace())
                newValue
            }

            UnaryOperator.POSTFIX_INCREMENT -> {
                // x++: save original, add 1, assign back, return original
                val target = unaryOp.operand
                if (target !is Identifier) {
                    throw KlangScriptTypeError(
                        message = "Postfix increment (++) requires an identifier target",
                        operation = "postfix ++",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val current = evaluate(target)
                if (current !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Postfix increment (++) requires a number, got ${current.toDisplayString()}",
                        operation = "postfix ++",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                env.assign(target.name, NumberValue(current.value + 1.0), unaryOp.location, getStackTrace())
                current  // Return original value
            }

            UnaryOperator.POSTFIX_DECREMENT -> {
                // x--: save original, subtract 1, assign back, return original
                val target = unaryOp.operand
                if (target !is Identifier) {
                    throw KlangScriptTypeError(
                        message = "Postfix decrement (--) requires an identifier target",
                        operation = "postfix --",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val current = evaluate(target)
                if (current !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Postfix decrement (--) requires a number, got ${current.toDisplayString()}",
                        operation = "postfix --",
                        location = unaryOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                env.assign(target.name, NumberValue(current.value - 1.0), unaryOp.location, getStackTrace())
                current  // Return original value
            }
        }
    }

    /**
     * Evaluate a binary operation expression
     *
     * Binary operations perform arithmetic, comparison, and logical operations.
     *
     * Process:
     * 1. For logical operators (&&, ||): use short-circuit evaluation
     * 2. For other operators: evaluate both operands, then apply operation
     * 3. Return the result
     *
     * Supported operators:
     * - Arithmetic (+, -, *, /, %): Require number operands, return NumberValue
     * - Comparison (<, <=, >, >=): Require number operands, return BooleanValue
     * - Equality (==, !=): Work on all types, return BooleanValue
     * - Logical (&&, ||): Short-circuit evaluation, return BooleanValue
     *
     * @param binOp The binary operation AST node
     * @return NumberValue for arithmetic, BooleanValue for comparisons and logical ops
     * @throws KlangScriptTypeError if operands are invalid for the operation
     *
     * Examples:
     * - 5 + 3 → NumberValue(8.0)
     * - 10 - 4 → NumberValue(6.0)
     * - 3 * 7 → NumberValue(21.0)
     * - 20 / 4 → NumberValue(5.0)
     * - 10 % 3 → NumberValue(1.0)
     * - 5 > 3 → BooleanValue(true)
     * - 10 == 10 → BooleanValue(true)
     * - "a" == "a" → BooleanValue(true)
     * - true && false → BooleanValue(false)
     * - true || false → BooleanValue(true)
     * - 1 / 0 → TypeError: "Division by zero"
     * - 1 % 0 → TypeError: "Modulo by zero"
     */
    private fun evaluateBinaryOp(binOp: BinaryOperation): RuntimeValue {
        // Handle logical operators with short-circuit evaluation
        when (binOp.operator) {
            BinaryOperator.AND -> {
                val leftValue = evaluate(binOp.left)
                // Short-circuit: if left is falsy, return false without evaluating right
                return if (!toBoolean(leftValue)) {
                    BooleanValue(false)
                } else {
                    val rightValue = evaluate(binOp.right)
                    BooleanValue(toBoolean(rightValue))
                }
            }

            BinaryOperator.OR -> {
                val leftValue = evaluate(binOp.left)
                // Short-circuit: if left is truthy, return true without evaluating right
                return if (toBoolean(leftValue)) {
                    BooleanValue(true)
                } else {
                    val rightValue = evaluate(binOp.right)
                    BooleanValue(toBoolean(rightValue))
                }
            }

            BinaryOperator.NULLISH_COALESCE -> {
                val leftValue = evaluate(binOp.left)
                // If left is null, evaluate and return right. Otherwise return left.
                return if (leftValue is NullValue) {
                    evaluate(binOp.right)
                } else {
                    leftValue
                }
            }

            else -> {
                // For all other operators, evaluate both operands
            }
        }

        // Evaluate both operands for non-logical operators
        val leftValue = evaluate(binOp.left)
        val rightValue = evaluate(binOp.right)

        // Handle comparison operators
        when (binOp.operator) {
            BinaryOperator.EQUAL -> {
                return BooleanValue(valuesEqual(leftValue, rightValue))
            }

            BinaryOperator.NOT_EQUAL -> {
                return BooleanValue(!valuesEqual(leftValue, rightValue))
            }

            BinaryOperator.STRICT_EQUAL -> {
                // Strict equality: same type AND same value; objects/arrays by reference
                return BooleanValue(valuesStrictEqual(leftValue, rightValue))
            }

            BinaryOperator.STRICT_NOT_EQUAL -> {
                return BooleanValue(!valuesStrictEqual(leftValue, rightValue))
            }

            BinaryOperator.IN -> {
                // "key" in obj — left must be StringValue, right must be ObjectValue
                if (leftValue !is StringValue) {
                    throw KlangScriptTypeError(
                        message = "Left operand of 'in' must be a string, got ${leftValue.toDisplayString()}",
                        operation = "in",
                        location = binOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                if (rightValue !is ObjectValue) {
                    throw KlangScriptTypeError(
                        message = "Right operand of 'in' must be an object, got ${rightValue.toDisplayString()}",
                        operation = "in",
                        location = binOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                return BooleanValue(leftValue.value in rightValue.properties)
            }

            BinaryOperator.LESS_THAN,
            BinaryOperator.LESS_THAN_OR_EQUAL,
            BinaryOperator.GREATER_THAN,
            BinaryOperator.GREATER_THAN_OR_EQUAL,
                -> {
                // Numeric comparison operators require both operands to be numbers
                if (leftValue !is NumberValue || rightValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Binary ${binOp.operator} operation requires numbers, " +
                                "got ${leftValue.toDisplayString()} and ${rightValue.toDisplayString()}",
                        operation = binOp.operator.toString(),
                        location = binOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val result = when (binOp.operator) {
                    BinaryOperator.LESS_THAN -> leftValue.value < rightValue.value
                    BinaryOperator.LESS_THAN_OR_EQUAL -> leftValue.value <= rightValue.value
                    BinaryOperator.GREATER_THAN -> leftValue.value > rightValue.value
                    BinaryOperator.GREATER_THAN_OR_EQUAL -> leftValue.value >= rightValue.value
                }
                return BooleanValue(result)
            }

            BinaryOperator.BITWISE_AND,
            BinaryOperator.BITWISE_OR,
            BinaryOperator.BITWISE_XOR,
            BinaryOperator.SHIFT_LEFT,
            BinaryOperator.SHIFT_RIGHT,
            BinaryOperator.UNSIGNED_SHIFT_RIGHT,
                -> {
                if (leftValue !is NumberValue || rightValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Binary ${binOp.operator} operation requires numbers, " +
                                "got ${leftValue.toDisplayString()} and ${rightValue.toDisplayString()}",
                        operation = binOp.operator.toString(),
                        location = binOp.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val l = leftValue.value.toInt()
                val r = rightValue.value.toInt()
                val result = when (binOp.operator) {
                    BinaryOperator.BITWISE_AND -> l and r
                    BinaryOperator.BITWISE_OR -> l or r
                    BinaryOperator.BITWISE_XOR -> l xor r
                    BinaryOperator.SHIFT_LEFT -> l shl r
                    BinaryOperator.SHIFT_RIGHT -> l shr r
                    BinaryOperator.UNSIGNED_SHIFT_RIGHT -> l ushr r
                }
                return NumberValue(result.toDouble())
            }

            else -> {
                // String concatenation: only when BOTH operands are strings.
                // KlangScript does NOT do implicit type coercion (number + string, null + string, etc.).
                // Use template literals `Hello ${name}` for mixed-type string building.
                if (binOp.operator == BinaryOperator.ADD &&
                    leftValue is StringValue && rightValue is StringValue
                ) {
                    return StringValue(leftValue.value + rightValue.value)
                }

                // Arithmetic operators - ensure both operands are numbers
                if (leftValue !is NumberValue || rightValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Binary ${binOp.operator} operation requires numbers, " +
                                "got ${leftValue.toDisplayString()} and ${rightValue.toDisplayString()}",
                        operation = binOp.operator.toString(),
                        location = binOp.location,
                        callStackTrace = getStackTrace()
                    )
                }

                // Perform the arithmetic operation
                val result = when (binOp.operator) {
                    BinaryOperator.ADD -> leftValue.value + rightValue.value
                    BinaryOperator.SUBTRACT -> leftValue.value - rightValue.value
                    BinaryOperator.MULTIPLY -> leftValue.value * rightValue.value
                    BinaryOperator.POWER -> leftValue.value.pow(rightValue.value)
                    BinaryOperator.DIVIDE -> {
                        // Check for division by zero
                        if (rightValue.value == 0.0) {
                            throw KlangScriptTypeError(
                                message = "Division by zero",
                                operation = "division",
                                location = binOp.location,
                                callStackTrace = getStackTrace()
                            )
                        }
                        leftValue.value / rightValue.value
                    }

                    BinaryOperator.MODULO -> {
                        // Check for modulo by zero (consistent with division)
                        if (rightValue.value == 0.0) {
                            throw KlangScriptTypeError(
                                message = "Modulo by zero",
                                operation = "modulo",
                                location = binOp.location,
                                callStackTrace = getStackTrace()
                            )
                        }
                        leftValue.value % rightValue.value
                    }
                }

                return NumberValue(result)
            }
        }
    }

    /**
     * Compare two runtime values for equality
     *
     * Implements value equality semantics:
     * - Numbers: compare by value
     * - Strings: compare by content
     * - Booleans: compare by value
     * - Null: null == null is true
     * - Different types: always false
     * - Objects/Arrays: compare by reference (same instance)
     *
     * @param left First value
     * @param right Second value
     * @return true if values are equal, false otherwise
     */
    private fun valuesEqual(left: RuntimeValue, right: RuntimeValue): Boolean {
        return when {
            // Both null
            left is NullValue && right is NullValue -> true
            // Both numbers
            left is NumberValue && right is NumberValue -> left.value == right.value
            // Both strings
            left is StringValue && right is StringValue -> left.value == right.value
            // Both booleans
            left is BooleanValue && right is BooleanValue -> left.value == right.value
            // Same object reference
            left is ObjectValue && right is ObjectValue -> left === right
            // Same array reference
            left is ArrayValue && right is ArrayValue -> left === right
            // Different types or values
            else -> false
        }
    }

    /**
     * Compare two runtime values for strict equality (===)
     *
     * Strict equality requires both same type and same value.
     * No type coercion is performed.
     * Objects and arrays are equal only if they are the same reference.
     *
     * @param left First value
     * @param right Second value
     * @return true if values are strictly equal, false otherwise
     */
    private fun valuesStrictEqual(left: RuntimeValue, right: RuntimeValue): Boolean {
        // Types must match exactly
        if (left::class != right::class) return false
        // Same type - use value equality (which already does reference equality for objects/arrays)
        return valuesEqual(left, right)
    }

    /**
     * Evaluate a member access expression
     *
     * Member access (dot notation) allows accessing properties on both
     * script objects and native Kotlin objects. This enables method chaining
     * and object-oriented patterns.
     *
     * Process:
     * 1. Evaluate the object expression
     * 2. If native object: Look up extension method, return BoundNativeMethod
     * 3. If script object: Return the property value (or NullValue if missing)
     * 4. Otherwise: Throw TypeError
     *
     * @param memberAccess The member access AST node
     * @return The runtime value of the property or bound method
     * @throws KlangScriptTypeError if the object doesn't support member access or property not found
     *
     * Examples:
     * ```
     * // Script object:
     * let obj = { name: "test", value: 42 }
     * obj.name  // Returns StringValue("test")
     *
     * // Native object:
     * let pattern = note("a b c")  // Returns NativeObjectValue<StrudelPattern>
     * pattern.sound  // Returns BoundNativeMethod for sound() extension
     *
     * // Method chaining:
     * note("c d e").sound("saw").gain(0.5)
     * // Each dot returns next method in chain
     * ```
     */
    private fun evaluateMemberAccess(memberAccess: MemberAccess): RuntimeValue {
        // Evaluate the object expression
        val objValue = evaluate(memberAccess.obj)

        // Optional chaining: if object is null, return null instead of throwing
        if (memberAccess.optional && objValue is NullValue) {
            return NullValue
        }

        // An operator symbol (`__invoke__`) is reached through its syntax only, never by name: no member.
        val isOperatorName = NativeOperatorNames.isOperatorName(memberAccess.property)

        // Handle native objects - lookup extension properties first, then methods
        if (objValue is NativeObjectValue<*>) {
            val extensionProperty = engine.getExtensionProperty(objValue, memberAccess.property)
            if (extensionProperty != null) {
                return extensionProperty.getter(objValue.value)
            }

            val extensionMethod = if (isOperatorName) null else engine.getExtensionMethod(objValue, memberAccess.property)
            if (extensionMethod != null) {
                // Return bound method
                return BoundNativeMethod(
                    methodName = memberAccess.property,
                    receiver = objValue,
                    invoker = { args, location -> extensionMethod.invoker(objValue.value, args, location, engine) },
                    paramSpecs = extensionMethod.paramSpecs,
                )
            }

            // Method not found - lead with the nearest name, THEN a truncated list. Dumping all
            // ~400 names unranked (the old behaviour) buries the one that matters: a single
            // adjacent transposition in a door name (2026-08-22) was invisible in the middle of the dump.
            val availableMethods = engine.getExtensionMethodNames(objValue)
            val suggestion = suggestNames(memberAccess.property, availableMethods) +
                    formatAvailableNames(availableMethods)

            throw KlangScriptTypeError(
                message = "Native type '${engine.getDisplayTypeName(objValue)}' has no method " +
                        "'${memberAccess.property}'.$suggestion",
                operation = "member access",
                location = memberAccess.location,
                astNode = memberAccess,
                callStackTrace = getStackTrace()
            )
        }
        // Every other value kind except a script object (number, string, boolean, array, null, a function) looks
        // up its registered extensions the same way. A kind with none registered falls through to the generic
        // error below. A script object keeps plain property access: its own properties are its members.
        else if (objValue !is ObjectValue) {
            val extensionProperty = engine.getExtensionProperty(objValue, memberAccess.property)
            if (extensionProperty != null) {
                return extensionProperty.getter(objValue)
            }

            val extensionMethod = if (isOperatorName) null else engine.getExtensionMethod(objValue, memberAccess.property)
            if (extensionMethod != null) {
                // Return bound method
                return BoundNativeMethod(
                    methodName = memberAccess.property,
                    receiver = NativeObjectValue.fromValue(objValue),
                    invoker = { args, location -> extensionMethod.invoker(objValue, args, location, engine) },
                    paramSpecs = extensionMethod.paramSpecs,
                )
            }

            // If there are registered extension methods for this type but the requested one doesn't exist,
            // throw a helpful error. Otherwise, fall through to the generic error below.
            val availableMethods = engine.getExtensionMethodNames(objValue)
            if (availableMethods.isNotEmpty()) {
                // Same treatment as the native-object branch above: sprudel registers String
                // extensions, so `"c d e".gian(...)` reaches HERE and used to get the same
                // unranked 4 KB dump this change exists to remove.
                val typeName = engine.getDisplayTypeName(objValue)
                val suggestion = suggestNames(memberAccess.property, availableMethods) +
                        formatAvailableNames(availableMethods)

                throw KlangScriptTypeError(
                    message = "Type '$typeName' has no method '${memberAccess.property}'.$suggestion",
                    operation = "member access",
                    location = memberAccess.location,
                    astNode = memberAccess,
                    callStackTrace = getStackTrace()
                )
            }
            // Otherwise, fall through to generic error handling
        }

        // Handle script objects
        if (objValue !is ObjectValue) {
            throw KlangScriptTypeError(
                message = "Cannot access property '${memberAccess.property}' on non-object value: ${objValue.toDisplayString()}",
                operation = "member access",
                location = memberAccess.location,
                astNode = memberAccess,
                callStackTrace = getStackTrace()
            )
        }

        // Return the property value
        return objValue.getProperty(memberAccess.property)
    }

    /**
     * Evaluate an object literal expression
     *
     * Object literals create new ObjectValue instances with properties
     * evaluated in the current environment.
     *
     * Process:
     * 1. Create empty ObjectValue
     * 2. For each property: evaluate the value expression
     * 3. Store the property in the object
     * 4. Return the object
     *
     * @param objectLiteral The object literal AST node
     * @return An ObjectValue with all properties evaluated
     *
     * Examples:
     * ```
     * // Script: { x: 10, y: 20 }
     * // Creates ObjectValue with x=10, y=20
     *
     * // Script: { name: getName(), age: 30 }
     * // Evaluates getName() first, then creates object
     *
     * // Script: { nested: { inner: 42 } }
     * // Recursively evaluates nested object literal
     * ```
     */
    private fun evaluateObjectLiteral(objectLiteral: ObjectLiteral): RuntimeValue {
        val properties = mutableMapOf<String, RuntimeValue>()

        // Evaluate each property value
        for ((key, valueExpr) in objectLiteral.properties) {
            val value = evaluate(valueExpr)
            properties[key] = value
        }

        return ObjectValue(properties)
    }

    /**
     * Evaluate an array literal expression
     *
     * Array literals create new ArrayValue instances at runtime.
     * Each element expression is evaluated and the results are collected into a mutable list.
     *
     * **Process:**
     * 1. Iterate through all element expressions in the array literal
     * 2. Evaluate each element expression in the current environment
     * 3. Collect all evaluated values into a mutable list
     * 4. Return an ArrayValue containing the elements
     *
     * **Supports:**
     * - Empty arrays: `[]` produces `ArrayValue(mutableListOf())`
     * - Any expression types: literals, variables, function calls, nested arrays, objects
     * - Nested evaluation: `[[1, 2], [3, 4]]` recursively evaluates inner arrays
     *
     * @param arrayLiteral The array literal AST node to evaluate
     * @return ArrayValue containing the evaluated element values
     *
     * Examples:
     * ```javascript
     * // Simple array
     * [1, 2, 3] -> ArrayValue([NumberValue(1), NumberValue(2), NumberValue(3)])
     *
     * // Mixed types
     * [1, "hello", true] -> ArrayValue([NumberValue(1), StringValue("hello"), BooleanValue(true)])
     *
     * // Expressions
     * let x = 5
     * [x, x + 1, x * 2] -> ArrayValue([NumberValue(5), NumberValue(6), NumberValue(10)])
     *
     * // Nested arrays
     * [[1, 2], [3, 4]] -> ArrayValue([
     *     ArrayValue([NumberValue(1), NumberValue(2)]),
     *     ArrayValue([NumberValue(3), NumberValue(4)])
     * ])
     *
     * // Arrays with function calls
     * [getValue(), calculate()] // Calls both functions, stores results
     * ```
     */
    private fun evaluateArrayLiteral(arrayLiteral: ArrayLiteral): RuntimeValue {
        val elements = mutableListOf<RuntimeValue>()

        // Evaluate each element expression
        for (elementExpr in arrayLiteral.elements) {
            val value = evaluate(elementExpr)
            elements.add(value)
        }

        return ArrayValue(elements)
    }

    /**
     * Evaluate an assignment expression: target = value, obj.prop = value, arr[ i ] = value
     *
     * @param assignment The assignment expression AST node
     * @return The assigned value
     */
    private fun evaluateAssignment(assignment: AssignmentExpression): RuntimeValue {
        val value = evaluate(assignment.value)

        return when (val target = assignment.target) {
            is Identifier -> {
                // Simple variable assignment: x = value
                env.assign(target.name, value, target.location, getStackTrace())
            }

            is MemberAccess -> {
                // Object property assignment: obj.prop = value
                val objValue = evaluate(target.obj)
                if (objValue !is ObjectValue) {
                    throw KlangScriptTypeError(
                        message = "Cannot assign property '${target.property}' on non-object value: ${objValue.toDisplayString()}",
                        operation = "member assignment",
                        location = assignment.location,
                        callStackTrace = getStackTrace()
                    )
                }
                objValue.setProperty(target.property, value)
                value
            }

            is IndexAccess -> {
                // Index assignment: arr[i] = value or obj["key"] = value
                val objValue = evaluate(target.obj)
                val indexValue = evaluate(target.index)

                when (objValue) {
                    is ArrayValue -> {
                        if (indexValue !is NumberValue) {
                            throw KlangScriptTypeError(
                                message = "Array index must be a number, got ${indexValue.toDisplayString()}",
                                operation = "index assignment",
                                location = assignment.location,
                                callStackTrace = getStackTrace()
                            )
                        }
                        val idx = indexValue.value.toInt()
                        if (idx < 0 || idx >= objValue.elements.size) {
                            throw KlangScriptTypeError(
                                message = "Array index $idx is out of bounds (size: ${objValue.elements.size})",
                                operation = "index assignment",
                                location = assignment.location,
                                callStackTrace = getStackTrace()
                            )
                        }
                        objValue.elements[idx] = value
                        value
                    }

                    is ObjectValue -> {
                        if (indexValue !is StringValue) {
                            throw KlangScriptTypeError(
                                message = "Object key must be a string, got ${indexValue.toDisplayString()}",
                                operation = "index assignment",
                                location = assignment.location,
                                callStackTrace = getStackTrace()
                            )
                        }
                        objValue.setProperty(indexValue.value, value)
                        value
                    }

                    else -> {
                        throw KlangScriptTypeError(
                            "Cannot index-assign on value: ${objValue.toDisplayString()}",
                            operation = "index assignment",
                            location = assignment.location,
                            callStackTrace = getStackTrace()
                        )
                    }
                }
            }

            else -> throw KlangScriptTypeError(
                message = "Invalid assignment target",
                operation = "assignment",
                location = assignment.location,
                callStackTrace = getStackTrace()
            )
        }
    }

    /**
     * Evaluate a ternary expression: condition ? thenExpr : elseExpr
     *
     * @param ternary The ternary expression AST node
     * @return The value of the then or else branch depending on condition
     */
    private fun evaluateTernary(ternary: TernaryExpression): RuntimeValue {
        val condition = evaluate(ternary.condition)
        return if (toBoolean(condition)) {
            evaluate(ternary.thenExpr)
        } else {
            evaluate(ternary.elseExpr)
        }
    }

    /**
     * Evaluate an index access expression: arr[ i ] or obj["key"]
     *
     * - ArrayValue: index must be NumberValue → returns element or NullValue if out of range
     * - ObjectValue: index must be StringValue → returns property or NullValue if missing
     *
     * @param indexAccess The index access AST node
     * @return The value at the given index/key
     */
    private fun evaluateIndexAccess(indexAccess: IndexAccess): RuntimeValue {
        val objValue = evaluate(indexAccess.obj)
        val indexValue = evaluate(indexAccess.index)

        return when (objValue) {
            is ArrayValue -> {
                if (indexValue !is NumberValue) {
                    throw KlangScriptTypeError(
                        message = "Array index must be a number, got ${indexValue.toDisplayString()}",
                        operation = "index access",
                        location = indexAccess.location,
                        callStackTrace = getStackTrace()
                    )
                }
                val idx = indexValue.value.toInt()
                if (idx < 0 || idx >= objValue.elements.size) {
                    NullValue
                } else {
                    objValue.elements[idx]
                }
            }

            is ObjectValue -> {
                if (indexValue !is StringValue) {
                    throw KlangScriptTypeError(
                        message = "Object key must be a string, got ${indexValue.toDisplayString()}",
                        operation = "index access",
                        location = indexAccess.location,
                        callStackTrace = getStackTrace()
                    )
                }
                objValue.getProperty(indexValue.value)
            }

            else -> throw KlangScriptTypeError(
                message = "Cannot index into value: ${objValue.toDisplayString()}",
                operation = "index access",
                location = indexAccess.location,
                callStackTrace = getStackTrace()
            )
        }
    }

    /**
     * Get the environment for external access
     *
     * This allows the KlangScript facade to register functions
     * in the interpreter's environment.
     *
     * @return The interpreter's environment
     */
    fun getEnvironment(): Environment = env
}
